/**
 * SPR-571 — Bundled "dummy" filter config for the Matches tabs.
 *
 * This object is the SAME SHAPE the BE (Active Admin) will eventually serve
 * from `GET /api/filter-configs/:tabKey`. Until that endpoint exists,
 * `filterConfigStore` returns this verbatim so the config-driven engine is
 * fully demonstrable today. See docs/SPR-571-BE-GAPS.md §F.
 *
 * Contract recap (so BE can mirror it 1:1):
 *   - `control`   → which FE control-type renders it (control-types.js)
 *   - `placement` → "column" | "toolbar" | "panel"
 *   - `options`   → { source: "static", items } | { source: "remote", masterKey, searchable, extraItems }
 *   - `serialize` → { strategy, ...params } → how the engine turns the picked
 *                   value(s) into the GraphQL filter fields (serializers.js)
 *
 * NOTE: rendering of `placement: toolbar | panel` controls from config is a
 * follow-up — for the prototype those still use the existing SPR-571
 * components, but their SERIALIZATION already runs through this config.
 */

import { TABLE_NAMES } from '~/common/constants/table-name.constant'

export const CANDIDATES_MATCH_TAB_KEY = TABLE_NAMES.CANDIDATES_MATCH

const ACCOUNT_TIER_ITEMS = [
	{ label: 'Alpha', value: 'alpha' },
	{ label: 'Key', value: 'key' },
	{ label: 'Standard', value: 'standard' },
]

const CREDENTIAL_READINESS_ITEMS = [
	{ label: 'Ready', value: 'ready' },
	{ label: 'Near-Ready', value: 'near_ready' },
	{ label: 'Not Ready', value: 'not_ready' },
]

const EDUCATION_SPECIALTY_ITEMS = [
	{ label: 'Speech-Language Pathologist (SLP)', value: 'slp' },
	{ label: 'Occupational Therapist (OT)', value: 'ot' },
	{ label: 'Physical Therapist (PT)', value: 'pt' },
	{ label: 'School Psychologist', value: 'school_psych' },
	{ label: 'Board Certified Behavior Analyst (BCBA)', value: 'bcba' },
]

/**
 * The Matches-tab config. `tabKey` is what FE passes to `filterConfigStore`.
 */
export const MATCH_FILTER_CONFIG = {
	tabKey: CANDIDATES_MATCH_TAB_KEY,
	version: 1,
	label: 'Candidate Matches',
	filters: [
		// 1.1 Account Tier
		{
			key: 'accountTier',
			label: 'Account Tier',
			control: 'multiSelect',
			placement: 'column',
			column: { title: 'Account Tier', minWidth: 160, flexGrow: 0.5, sortable: true },
			cellKey: 'accountTier',
			cell: { type: 'capitalize', field: 'accountTier' },
			options: { source: 'static', items: ACCOUNT_TIER_ITEMS },
			serialize: { strategy: 'array', field: 'accountTiers' },
		},
		// 1.2 Setting (Job.setting picklist)
		{
			key: 'jobSettingIds',
			label: 'Setting',
			control: 'multiSelect',
			placement: 'column',
			column: { title: 'Setting', minWidth: 180, flexGrow: 0.6, sortable: true },
			cellKey: 'jobSettings',
			cell: { type: 'joinList', field: 'jobSettings' },
			options: { source: 'remote', masterKey: 'jobSettings', searchable: true },
			serialize: { strategy: 'array', field: 'jobSettingIds' },
		},
		// 1.3 Credential Readiness (BLOCKED on Eitan source-field discovery)
		{
			key: 'credentialReadiness',
			label: 'Credential Readiness',
			control: 'multiSelect',
			placement: 'column',
			column: { title: 'Credential Readiness', minWidth: 200, flexGrow: 0.6, sortable: true },
			cellKey: 'credentialReadiness',
			cell: { type: 'mapLabel', field: 'credentialReadiness' },
			options: { source: 'static', items: CREDENTIAL_READINESS_ITEMS },
			serialize: { strategy: 'array', field: 'credentialReadiness' },
		},
		// 1.4 License State — CONTAINS-against-job
		{
			key: 'licenseStateMatchesJob',
			label: 'License State',
			control: 'multiSelect',
			placement: 'column',
			column: { title: 'License State', minWidth: 200, flexGrow: 0.8, sortable: true },
			cellKey: 'licenseStates',
			options: { source: 'remote', masterKey: 'licenseStates', searchable: true },
			serialize: { strategy: 'licenseContains' },
		},
		// 1.5 Assigned Recruiter (+ Unassigned sentinel)
		{
			key: 'employerIds',
			label: 'Assigned Recruiter',
			control: 'multiSelect',
			placement: 'column',
			column: { title: 'Assigned Recruiter', minWidth: 350, flexGrow: 1, sortable: true },
			cellKey: 'employerName',
			options: {
				source: 'remote',
				masterKey: 'employerNames',
				searchable: true,
				extraItems: [{ label: 'Unassigned', value: '__unassigned__' }],
			},
			serialize: { strategy: 'recruiterWithUnassigned', unassignedValue: '__unassigned__' },
		},
		// 1.5 My Matches toggle
		{
			key: 'myMatches',
			label: 'My Matches',
			control: 'toggle',
			placement: 'toolbar',
			group: 'Scope',
			serialize: { strategy: 'assignedToMe' },
		},
		// 3.1 Assignment-End Window
		{
			key: 'assignmentEndWindow',
			label: 'Assignment ends in',
			control: 'chipSelect',
			placement: 'toolbar',
			group: 'Redeployment',
			options: {
				source: 'static',
				items: [
					{ label: 'Next 14 days', value: '14' },
					{ label: 'Next 30 days', value: '30' },
					{ label: 'Next 60 days', value: '60' },
					{ label: 'Next 90 days', value: '90' },
				],
			},
			serialize: { strategy: 'intField', field: 'assignmentEndingWithinDays' },
		},
		// 3.2 Education specialty sub-filter
		{
			key: 'educationSpecialty',
			label: 'Education Specialty',
			control: 'multiSelect',
			placement: 'column',
			column: { title: 'Education Specialty', minWidth: 220, flexGrow: 0.6, sortable: false },
			cellKey: 'educationSpecialty',
			cell: { type: 'joinList', fields: ['educationSpecialties'] },
			options: { source: 'static', items: EDUCATION_SPECIALTY_ITEMS },
			serialize: { strategy: 'array', field: 'educationSpecialties' },
		},
		// 3.2 Bilingual
		{
			key: 'bilingual',
			label: 'Bilingual',
			control: 'multiSelect',
			placement: 'column',
			column: { title: 'Bilingual', minWidth: 140, flexGrow: 0.4, sortable: false, align: 'center' },
			cellKey: 'bilingual',
			cell: { type: 'boolean', field: 'bilingual' },
			options: {
				source: 'static',
				items: [
					{ label: 'Bilingual', value: 'true' },
					{ label: 'Not bilingual', value: 'false' },
				],
			},
			serialize: { strategy: 'boolField', field: 'bilingual' },
		},
		// 2.1 Engagement — days since last login
		{
			key: 'engagement_daysSinceLastLogin',
			label: 'Last login within',
			control: 'numberThreshold',
			placement: 'panel',
			group: 'Engagement signals',
			serialize: { strategy: 'numberField', field: 'daysSinceLastLoginMax', valueShape: 'max' },
		},
		// 2.1 Engagement — jobs viewed (windowed)
		{
			key: 'engagement_jobsViewed',
			label: 'Jobs viewed',
			control: 'windowedThreshold',
			placement: 'panel',
			group: 'Engagement signals',
			windows: [7, 14, 30],
			serialize: { strategy: 'windowedMin', fieldTemplate: 'jobsViewedLast{window}DaysMin' },
		},
		// 2.1 Engagement — jobs saved (windowed)
		{
			key: 'engagement_jobsSaved',
			label: 'Jobs saved',
			control: 'windowedThreshold',
			placement: 'panel',
			group: 'Engagement signals',
			windows: [7, 14, 30],
			serialize: { strategy: 'windowedMin', fieldTemplate: 'jobsSavedLast{window}DaysMin' },
		},
		// 2.1 Engagement — messages opened (windowed)
		{
			key: 'engagement_messagesOpened',
			label: 'Messages opened',
			control: 'windowedThreshold',
			placement: 'panel',
			group: 'Engagement signals',
			windows: [7, 14, 30],
			serialize: { strategy: 'windowedMin', fieldTemplate: 'messagesOpenedLast{window}DaysMin' },
		},
		// 2.1 Engagement — profile updated since
		{
			key: 'engagement_profileUpdatedSince',
			label: 'Profile updated since',
			control: 'dateSince',
			placement: 'panel',
			group: 'Engagement signals',
			serialize: { strategy: 'dateField', field: 'profileUpdatedSince' },
		},
	],
	savedViews: { enabled: true },

	// SPR-571 (UX): one-click preset recipes. Same shape applyMatchPreset
	// consumes; moving them here lets each tab/role ship its own set from BE.
	presets: [
		{
			id: 'high-value-queue',
			emoji: '🔥',
			name: 'High-value queue',
			description: 'Alpha + Key tier · Ready / Near-Ready · Active in last 14 days',
			sortField: 'engagementScore',
			sortOrder: 'desc',
			filters: {
				accountTier: [
					{ label: 'Alpha', value: 'alpha' },
					{ label: 'Key', value: 'key' },
				],
				credentialReadiness: [
					{ label: 'Ready', value: 'ready' },
					{ label: 'Near-Ready', value: 'near_ready' },
				],
				engagement_daysSinceLastLogin: [{ label: 'Last login ≤ 14d', value: { max: 14 } }],
			},
		},
		{
			id: 'redeploy',
			emoji: '📅',
			name: 'Redeploy this month',
			description: 'Assignment ending within 30 days',
			sortField: 'engagementScore',
			sortOrder: 'desc',
			filters: {
				assignmentEndWindow: [{ label: 'Next 30 days', value: '30' }],
			},
		},
		{
			id: 'hot-prospects',
			emoji: '🆕',
			name: 'Hot prospects',
			description: 'Jobs viewed ≥5 in last 7 days · Last login ≤3d',
			sortField: 'engagementScore',
			sortOrder: 'desc',
			filters: {
				engagement_jobsViewed: [{ label: 'Jobs viewed (Last 7 days) ≥ 5', value: { window: 7, min: 5 } }],
				engagement_daysSinceLastLogin: [{ label: 'Last login ≤ 3d', value: { max: 3 } }],
			},
		},
		{
			id: 'cooling-off',
			emoji: '⏳',
			name: 'Cooling off',
			description: 'No login in 14+ days — needs reactivation outreach',
			sortField: 'engagementScore',
			sortOrder: 'asc',
			filters: {
				engagement_jobsSaved: [{ label: 'Jobs saved (Last 14 days) ≥ 0 (cold)', value: { window: 14, min: 0 } }],
			},
		},
	],

	// SPR-571 (UX): breakdown chip rail. Each chip counts workers whose
	// `field` equals `value`, and clicking it applies { filterKey: value }.
	breakdown: [
		{ label: 'Alpha', filterKey: 'accountTier', value: 'alpha', field: 'accountTier', color: '#DC2626' },
		{ label: 'Key', filterKey: 'accountTier', value: 'key', field: 'accountTier', color: '#F59E0B' },
		{ label: 'Ready', filterKey: 'credentialReadiness', value: 'ready', field: 'credentialReadiness', color: '#10B981' },
		{ label: 'Near-Ready', filterKey: 'credentialReadiness', value: 'near_ready', field: 'credentialReadiness', color: '#3B82F6' },
		{ label: 'Bilingual', filterKey: 'bilingual', value: 'true', field: 'bilingual', color: '#8B5CF6' },
	],
}

/**
 * Home → Matches tab. Same filter recipe as candidates-match (worker domain),
 * just a different tabKey. Reuses the same filter defs verbatim.
 */
export const HOME_MATCH_TAB_KEY = TABLE_NAMES.HOME_MATCH

export const HOME_MATCH_CONFIG = {
	...MATCH_FILTER_CONFIG,
	tabKey: HOME_MATCH_TAB_KEY,
	label: 'Home Matches',
}

/**
 * Job Info → Matches tab. Same worker-domain filter recipe, per-job query.
 */
export const JOB_DETAIL_MATCH_TAB_KEY = TABLE_NAMES.JOB_DETAIL_MATCH

export const JOB_DETAIL_MATCH_CONFIG = {
	...MATCH_FILTER_CONFIG,
	tabKey: JOB_DETAIL_MATCH_TAB_KEY,
	label: 'Job Matches',
}

/**
 * Registry of every bundled config, keyed by tabKey. `filterConfigStore`
 * falls back to this map when the BE endpoint is unavailable.
 */
export const BUNDLED_FILTER_CONFIGS = {
	[CANDIDATES_MATCH_TAB_KEY]: MATCH_FILTER_CONFIG,
	[HOME_MATCH_TAB_KEY]: HOME_MATCH_CONFIG,
	[JOB_DETAIL_MATCH_TAB_KEY]: JOB_DETAIL_MATCH_CONFIG,
}
